import { apiService } from './apiService';
import { patientsService } from './patientsService';
import { Proposal } from '../models/clinicAgent';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.eternal-health.com/api';

// Proposal patient_id is a UserProfile id. Two endpoints (activation,
// welcome-email) resolve their path id as the patient's AdminUser id instead,
// so those need a profile-id -> userId map (from GET /api/clinic/patient/).
export type ProfileIdToUserId = Map<number, number>;

// Human, Spanish titles for proposal cards.
const ACTION_TITLES: Record<string, string> = {
  create_diary_item: 'Crear elemento de diario',
  update_diary_item: 'Actualizar elemento de diario',
  create_meal: 'Crear comida',
  create_ingredient: 'Crear ingrediente',
  create_reminder: 'Crear recordatorio',
  set_patient_activation: 'Cambiar activación del paciente',
  send_welcome_email: 'Enviar email de bienvenida',
};

export function proposalTitle(action: string): string {
  return ACTION_TITLES[action] || action;
}

// Whether we know how to apply this proposal. Unknown / generic fallback
// actions are rendered read-only with no Confirm button.
export function isApplicable(action: string): boolean {
  return action in ACTION_TITLES;
}

function resolveUserId(profileId: number | null, map: ProfileIdToUserId): number {
  if (profileId == null) throw new Error('Falta el id del paciente para esta acción.');
  const userId = map.get(profileId);
  if (userId == null) {
    throw new Error('No se pudo resolver el paciente. Recarga la lista de pacientes e inténtalo de nuevo.');
  }
  return userId;
}

// An inline meal to create before posting the diary item that references it.
interface NewMeal {
  name: string;
  ingredients: { ingredient_id: number; quantity: number }[];
  [key: string]: unknown;
}

// Free-form outcome detail echoed back to the agent (e.g. the created entity id).
export type ProposalDetail = Record<string, unknown> | undefined;

// Apply a confirmed proposal by calling the matching clinic REST endpoint.
// The payload is model-generated and already shaped for the endpoint; the
// backend does final validation + re-checks the patient is assigned to the
// clinician. Throws on failure (error.response carries field errors). Returns
// a detail object (the created entity id when available) for proposal_result.
export async function applyProposal(p: Proposal, profileIdToUserId: ProfileIdToUserId): Promise<ProposalDetail> {
  const pid = p.patient_id;
  const payload = p.payload || {};

  switch (p.action) {
    case 'create_diary_item': {
      const body: Record<string, unknown> = { ...payload };
      // Inline meals must be created first, then their ids merged into `meals`.
      const newMeals = body.new_meals as NewMeal[] | undefined;
      if (Array.isArray(newMeals) && newMeals.length > 0) {
        const createdIds: number[] = [];
        for (const meal of newMeals) {
          const created = await apiService.post(`${API_BASE_URL}/clinic/patient/${pid}/meals/`, meal);
          if (created?.id != null) createdIds.push(created.id);
        }
        const existing = Array.isArray(body.meals) ? (body.meals as number[]) : [];
        body.meals = [...existing, ...createdIds];
        delete body.new_meals;
      }
      const created = await apiService.post(`${API_BASE_URL}/clinic/patient/${pid}/diary-items/create/`, body);
      return created?.id != null ? { diary_item_id: created.id } : undefined;
    }

    case 'update_diary_item': {
      const diaryItemId = (payload as any).diary_item_id;
      await apiService.patch(`${API_BASE_URL}/clinic/patient/${pid}/diary-items/${diaryItemId}/`, payload);
      return { diary_item_id: diaryItemId };
    }

    case 'create_meal': {
      const created = await apiService.post(`${API_BASE_URL}/clinic/patient/${pid}/meals/`, payload);
      return created?.id != null ? { meal_id: created.id } : undefined;
    }

    case 'create_ingredient': {
      // Global catalog; patient_id is null.
      const created = await apiService.post(`${API_BASE_URL}/clinic/ingredients/`, payload);
      return created?.id != null ? { ingredient_id: created.id } : undefined;
    }

    case 'create_reminder': {
      const created = await apiService.post(`${API_BASE_URL}/clinic/patient/${pid}/reminders/`, payload);
      return created?.id != null ? { reminder_id: created.id } : undefined;
    }

    case 'set_patient_activation': {
      // Path id is the AdminUser id, not the profile id.
      const userId = resolveUserId(pid, profileIdToUserId);
      const isActive = (payload as any).is_active;
      await apiService.patch(`${API_BASE_URL}/clinic/patient/${userId}/activation/`, { is_active: isActive });
      return { is_active: isActive };
    }

    case 'send_welcome_email': {
      // Backend resolves this path id as the AdminUser id as well.
      const userId = resolveUserId(pid, profileIdToUserId);
      const welcomeEmailType = (payload as any).welcomeEmailType;
      await patientsService.sendWelcomeEmail(userId, welcomeEmailType);
      return undefined;
    }

    default:
      throw new Error(`Acción no soportada: ${p.action}`);
  }
}
